import { useQuery, useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useEffect } from "react";
import { api, ApiError } from "./api/client";
import { AppUser } from "./api/types";
import { LoadingState } from "./components/Feedback";
import { applyBrand, currentBrand } from "./config/brand";
import { LoginScreen } from "./features/auth/LoginScreen";
import { TermsAcceptanceModal } from "./features/auth/TermsAcceptanceModal";

const AuthenticatedApp = lazy(() => import("./AuthenticatedApp"));

export function App() {
  const queryClient = useQueryClient();
  useEffect(() => applyBrand(currentBrand()), []);
  const auth = useQuery({
    queryKey: ["auth"],
    queryFn: async () => {
      try {
        return (await api<{ user: AppUser }>("/auth/me")).user;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
  });

  if (auth.isPending) return <div className="app-loading"><LoadingState /></div>;
  if (!auth.data) return <LoginScreen onAuthenticated={(user) => queryClient.setQueryData(["auth"], user)} />;

  const user = auth.data;
  async function logout() {
    try {
      await api("/auth/logout", { method: "POST" });
    } finally {
      queryClient.setQueryData(["auth"], null);
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "auth" });
    }
  }

  if (user.requiresTermsAcceptance) {
    return (
      <TermsAcceptanceModal
        user={user}
        onAccepted={(updatedUser) => queryClient.setQueryData(["auth"], updatedUser)}
        onLogout={logout}
      />
    );
  }

  return (
    <Suspense fallback={<div className="app-loading"><LoadingState /></div>}>
      <AuthenticatedApp user={user} onLogout={logout} />
    </Suspense>
  );
}